import { useEffect, useMemo, useState } from "react";
import {
  getMarketplaceLicenses,
  uploadMarketplaceOnboardingLicense,
} from "../api/marketplace.js";
import {
  groupDocsByType,
  parseRequestedAt,
  validateLicenseFile,
} from "../partials/licenses/licenseUtils.js";

/**
 * Loads the current seller's uploaded license documents and handles uploads.
 * Shared by the onboarding licenses step and the Settings → Licenses page.
 * @param {{onLoadError?: (message: string) => void}} [options]
 */
export default function useLicenseDocuments({ onLoadError } = {}) {
  const [docs, setDocs] = useState([]);
  const [licenseAnswers, setLicenseAnswers] = useState(null);
  const [licensesRequestedAt, setLicensesRequestedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const loadDocs = async () => {
    const res = await getMarketplaceLicenses();
    const list = Array.isArray(res?.licenses) ? res.licenses : [];
    setDocs(list);
    setLicenseAnswers(res?.license_answers ?? null);
    setLicensesRequestedAt(parseRequestedAt(res?.license_requested_at));
    return list;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadDocs();
      } catch (e) {
        if (!cancelled) {
          onLoadError?.(
            e?.response?.data?.message || e.message || "Failed to load licenses"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const docsByType = useMemo(() => groupDocsByType(docs), [docs]);

  const upload = async (type, file) => {
    if (!file) return;
    setUploadError(null);
    const invalid = validateLicenseFile(file, docsByType[type]);
    if (invalid) {
      setUploadError(invalid);
      return;
    }
    setUploadingType(type);
    try {
      await uploadMarketplaceOnboardingLicense(type, file);
      await loadDocs();
    } catch (e) {
      setUploadError(e?.response?.data?.message || e.message || "Upload failed");
    } finally {
      setUploadingType(null);
    }
  };

  return {
    docs,
    docsByType,
    licenseAnswers,
    licensesRequestedAt,
    loading,
    uploadingType,
    uploadError,
    setUploadError,
    upload,
    reload: loadDocs,
  };
}
